import React, { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useHistory } from 'react-router-dom';
import { getInstructorDetail } from '../api';
import CareerList from './CareerList';
import TopLectureBand from './TopLectureBand';
import itemDefaultImage from '../../../Assets/Images/item-default-image.png';

const onThumbError = (e) => {
  e.currentTarget.onerror = null;
  e.currentTarget.src = itemDefaultImage;
};

const TRANSITION_MS = 280;

/* 모바일용 강의 썸네일 */
const MobileLectureThumb = ({ id, title, thumbnailUrl }) => {
  const history = useHistory();
  return (
    <div
      className="relative shrink-0 w-[120px] h-[90px] overflow-hidden bg-[#3a3a3a] cursor-pointer rounded-[4px] group"
      onClick={() => history.push(`/lecture/${id}`)}
    >
      <img
        src={thumbnailUrl || itemDefaultImage}
        alt={title}
        className="w-full h-full object-cover"
        onError={onThumbError}
      />
      <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-200 pointer-events-none" />
    </div>
  );
};

const InstructorModal = ({ instructorId, isOpen, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  const isMobile = windowWidth < 768;
  const desktopScale = Math.min(1, (windowWidth - 32) / 1200);
  const scaledW = Math.round(1200 * desktopScale);
  const scaledH = Math.round(800 * desktopScale);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // isOpen prop으로 fade in/out 제어 — 뒤로가기 시에도 애니메이션 동작
  useEffect(() => {
    if (isOpen) {
      const id = requestAnimationFrame(() => setShow(true));
      return () => cancelAnimationFrame(id);
    } else {
      setShow(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setShow(false);
    setTimeout(onClose, TRANSITION_MS);
  }, [onClose]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    getInstructorDetail(instructorId)
      .then((data) => {
        if (mounted) setDetail(data);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [instructorId]);

  useEffect(() => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPadding = document.body.style.paddingRight;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = `${scrollbarWidth}px`;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPadding;
      document.removeEventListener('keydown', onKey);
    };
  }, [handleClose]);

  /* 로딩 / 에러 */
  const loadingEl = (type) => (
    <div
      className="flex items-center justify-center font-pretendard text-[#888]"
      style={{ height: type === 'mobile' ? 200 : '100%', fontSize: type === 'mobile' ? 18 : 24 }}
    >
      {loading ? '불러오는 중...' : '강사 정보를 불러올 수 없습니다.'}
    </div>
  );

  /* ── 모바일 레이아웃 (바텀시트) ── */
  const mobileModal = (
    <div
      className="fixed inset-0 z-[9999] bg-black/50 flex items-end"
      style={{ transition: `opacity ${TRANSITION_MS}ms ease`, opacity: show ? 1 : 0 }}
      onClick={handleClose}
    >
      <div
        className="relative w-full bg-white overflow-hidden"
        style={{
          maxHeight: '92vh',
          transition: `transform ${TRANSITION_MS}ms ease`,
          transform: show ? 'translateY(0)' : 'translateY(100%)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close — 원형 × 버튼 */}
        <button
          type="button"
          onClick={handleClose}
          aria-label="닫기"
          className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full flex items-center justify-center border-none cursor-pointer"
          style={{
            background: 'rgba(255,255,255,0.9)',
            color: '#1a1a1a',
            fontSize: 18,
            boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <line x1="1" y1="1" x2="11" y2="11" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" />
            <line x1="11" y1="1" x2="1" y2="11" stroke="#1a1a1a" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>

        {loading || !detail ? (
          loadingEl('mobile')
        ) : (
          <div className="overflow-y-auto" style={{ maxHeight: '92vh' }}>
            {/* 프로필 이미지 — 동그란 아바타 */}
            <div className="w-full flex items-end justify-center" style={{ height: '46vw', paddingBottom: '4vw' }}>
              <div
                className="rounded-full overflow-hidden flex items-center justify-center shrink-0"
                style={{
                  width: '34vw',
                  height: '34vw',
                  background: 'linear-gradient(180deg, #FFF0F3 0%, #FFB0C4 100%)',
                }}
              >
                {detail.profileImageUrl ? (
                  <img
                    src={detail.profileImageUrl}
                    alt={detail.name}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <span className="font-pretendard font-bold text-white select-none" style={{ fontSize: '10vw' }}>
                    {detail.name?.[0]}
                  </span>
                )}
              </div>
            </div>

            {/* 이름 / 전문 분야 */}
            <div className="px-5 pt-4 pb-3">
              <h2 className="font-pretendard m-0 leading-snug text-black" style={{ letterSpacing: '-0.04em' }}>
                <span style={{ fontSize: 20, fontWeight: 600 }}>{detail.name}</span>
                <span style={{ fontSize: 20, fontWeight: 400 }}> | </span>
                <span style={{ fontSize: 16, fontWeight: 400 }}>{detail.specialty}</span>
              </h2>
            </div>

            {/* 경력 */}
            <div className="px-5 pb-6">
              <CareerList careerItems={detail.careerItems} mobile />
            </div>

            {/* 대표 강의 밴드 */}
            <div className="w-full bg-[#4A4A4A] px-5 py-4 flex flex-row items-start gap-3">
              <span
                className="font-pretendard font-bold text-white shrink-0"
                style={{ fontSize: 16, letterSpacing: '-0.04em' }}
              >
                대표 강의 |
              </span>
              {detail.topLectures?.length > 0 ? (
                <div className="flex flex-row items-center gap-3 overflow-x-auto">
                  {detail.topLectures.slice(0, 4).map((lec) => (
                    <MobileLectureThumb
                      key={lec.id}
                      id={lec.id}
                      title={lec.title}
                      thumbnailUrl={lec.thumbnailUrl || lec.image_thumb}
                    />
                  ))}
                </div>
              ) : (
                <span className="font-pretendard text-white/60" style={{ fontSize: 14 }}>
                  등록된 대표 강의가 없습니다.
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  /* ── 데스크탑 레이아웃 (scale 기반 축소) ── */
  const desktopModal = (
    <div
      className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center"
      style={{ transition: `opacity ${TRANSITION_MS}ms ease`, opacity: show ? 1 : 0 }}
      onClick={handleClose}
    >
      {/* 외부: 실제 시각적 크기 */}
      <div
        style={{
          width: scaledW,
          height: scaledH,
          overflow: 'hidden',
          transition: `transform ${TRANSITION_MS}ms ease`,
          transform: show ? 'scale(1)' : 'scale(0.96)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 내부: 1200×800 원본, scale 축소 */}
        <div
          className="relative overflow-hidden bg-white"
          style={{
            width: 1200,
            height: 800,
            transform: `scale(${desktopScale})`,
            transformOrigin: 'top left',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
          }}
        >
          {/* Close */}
          <button
            type="button"
            onClick={handleClose}
            className="absolute z-20 font-pretendard font-medium text-black bg-transparent border-none cursor-pointer p-0 underline"
            style={{ left: 1089, top: 23, fontSize: 32, lineHeight: '38px', letterSpacing: '-0.04em' }}
          >
            Close
          </button>

          {loading || !detail ? (
            loadingEl('desktop')
          ) : (
            <>
              {/* Ellipse */}
              <div
                className="absolute z-0 rounded-full pointer-events-none"
                style={{
                  left: 542,
                  top: 171,
                  width: 759,
                  height: 759,
                  background: 'linear-gradient(180deg, #FFE5EA 0%, #FF98AA 100%)',
                }}
              />

              {/* 강사 사진 */}
              {detail.profileImageUrl && (
                <div
                  className="absolute z-10 overflow-hidden pointer-events-none"
                  style={{ left: 640, top: 50, width: 480, height: 720 }}
                >
                  <img
                    src={detail.profileImageUrl}
                    alt={detail.name}
                    className="block w-full h-full object-cover object-top"
                  />
                </div>
              )}

              {/* 하단 밴드 */}
              <TopLectureBand topLectures={detail.topLectures} />

              {/* 제목 */}
              <h2
                className="absolute z-20 font-pretendard text-black m-0 whitespace-nowrap"
                style={{ left: 50, top: 61, lineHeight: '48px', fontWeight: 600, letterSpacing: '-0.04em' }}
              >
                <span style={{ fontSize: 40 }}>{detail.name}</span>
                <span style={{ fontSize: 40, fontWeight: 400 }}> | </span>
                <span style={{ fontSize: 32 }}>{detail.specialty}</span>
              </h2>

              {/* 경력 목록 — 스크롤바 숨김 */}
              <div
                className="absolute z-20 overflow-y-auto [&::-webkit-scrollbar]:hidden"
                style={{
                  left: 50,
                  bottom: 270,
                  width: 540,
                  maxHeight: 400,
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                <CareerList careerItems={detail.careerItems} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(isMobile ? mobileModal : desktopModal, document.body);
};

export default InstructorModal;
